import { getApiUrl } from '../config/api';
import AdminKeyService from './AdminKeyService';
import EncryptionService from './EncryptionService';
import EncryptionKeyService from './EncryptionKeyService';
import DecryptionError from './DecryptionError';
import NotFoundError from './NotFoundError';
import UrlService from './UrlService';

class ApiService {
    // Helper method to get headers with admin keys
    getHeaders(electionId, voteId) {
        const headers = {
            'Content-Type': 'application/json',
        };

        // Add election admin key if available
        if (electionId) {
            const electionAdminKey = AdminKeyService.getElectionAdminKey(electionId);
            if (electionAdminKey) {
                headers['X-Election-Admin-Key'] = electionAdminKey;
            }
        }

        // Add vote admin key if available
        if (voteId) {
            const voteAdminKey = AdminKeyService.getVoteAdminKey(voteId);
            if (voteAdminKey) {
                headers['X-Vote-Admin-Key'] = voteAdminKey;
            }
        }

        return headers;
    }

    // Encryption helper methods
    async encryptElectionData(electionData, encryptionParams) {
        const encrypted = { ...electionData };

        // Encrypt title
        if (encrypted.title) {
            encrypted.title = await EncryptionService.encryptText(encrypted.title, encryptionParams);
        }

        // Encrypt description
        if (encrypted.description) {
            encrypted.description = await EncryptionService.encryptText(encrypted.description, encryptionParams);
        }

        // Encrypt option names
        if (encrypted.options && Array.isArray(encrypted.options)) {
            encrypted.options = await Promise.all(
                encrypted.options.map(async (option) => ({
                    ...option,
                    name: option.name ? await EncryptionService.encryptText(option.name, encryptionParams) : option.name
                }))
            );
        }

        return encrypted;
    }

    async decryptElectionData(electionData, encryptionParams) {
        const decrypted = { ...electionData };

        // Decrypt title
        if (decrypted.title) {
            decrypted.title = await EncryptionService.decryptText(decrypted.title, encryptionParams);
        }

        // Decrypt description
        if (decrypted.description) {
            decrypted.description = await EncryptionService.decryptText(decrypted.description, encryptionParams);
        }

        // Decrypt option names
        if (decrypted.options && Array.isArray(decrypted.options)) {
            decrypted.options = await Promise.all(
                decrypted.options.map(async (option) => ({
                    ...option,
                    name: option.name ? await EncryptionService.decryptText(option.name, encryptionParams) : option.name
                }))
            );
        }

        return decrypted;
    }

    async encryptVoteData(voteData, encryptionParams) {
        const encrypted = { ...voteData };

        // Encrypt voterName
        if (encrypted.voterName) {
            encrypted.voterName = await EncryptionService.encryptText(encrypted.voterName, encryptionParams);
        }

        // Encrypt option names within the vote's options array (if present)
        if (encrypted.options && Array.isArray(encrypted.options)) {
            encrypted.options = await Promise.all(
                encrypted.options.map(async (option) => ({
                    ...option,
                    name: option.name ? await EncryptionService.encryptText(option.name, encryptionParams) : option.name
                }))
            );
        }

        return encrypted;
    }

    async decryptVoteData(voteData, encryptionParams) {
        const decrypted = { ...voteData };

        // Decrypt voterName
        if (decrypted.voterName) {
            decrypted.voterName = await EncryptionService.decryptText(decrypted.voterName, encryptionParams);
        }

        // Decrypt option names within the vote's options array (if present)
        if (decrypted.options && Array.isArray(decrypted.options)) {
            decrypted.options = await Promise.all(
                decrypted.options.map(async (option) => ({
                    ...option,
                    name: option.name ? await EncryptionService.decryptText(option.name, encryptionParams) : option.name
                }))
            );
        }

        return decrypted;
    }

    /**
     * Handle decryption failures by attempting recovery with URL parameters.
     * If URL params differ from localStorage and work, updates localStorage.
     * Throws DecryptionError if recovery fails.
     */
    async handleDecryptionFailure(data, electionId, decryptFunction, localStorageParams) {
        // Get encryption params from URL
        const urlParams = UrlService.getEncryptionParamsFromUrl();

        // Check if URL params differ from localStorage params
        const paramsDiffer = urlParams && (
            !localStorageParams ||
            urlParams.scheme !== localStorageParams.scheme ||
            urlParams.key !== localStorageParams.key
        );

        // If URL params exist and differ, try decrypting with them
        if (paramsDiffer) {
            try {
                // Import URL params before using them for decryption
                const importedUrlParams = await EncryptionService.importParams(urlParams);
                const decryptedWithUrl = await decryptFunction(data, importedUrlParams);

                // Success! Update localStorage with URL params
                EncryptionKeyService.setEncryptionKey(electionId, urlParams);

                return decryptedWithUrl;
            } catch (urlError) {
                // URL params also failed - throw error indicating both failed
                throw new DecryptionError(
                    'Failed to decrypt data with both localStorage and URL encryption parameters',
                    electionId,
                    'both'
                );
            }
        }

        // No URL params or they're the same as localStorage - decryption failed definitively
        throw new DecryptionError(
            'Failed to decrypt data - encryption key may be missing or incorrect',
            electionId,
            localStorageParams ? 'localStorage' : 'missing'
        );
    }

    // Election API methods
    async createElection(electionData) {
        // Always create new elections as encrypted
        const dataToSend = { ...electionData, isEncrypted: true };

        // Generate encryption parameters
        const encryptionParams = await EncryptionService.generateEncryptionParams();

        // Encrypt the election data
        const encryptedData = await this.encryptElectionData(dataToSend, encryptionParams);

        const response = await fetch(getApiUrl('/api/ranked-elections'), {
            method: 'POST',
            headers: this.getHeaders(),
            body: JSON.stringify(encryptedData),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        // Store admin key if election is protected
        if (result.isProtected && result.adminKey) {
            AdminKeyService.setElectionAdminKey(result.id, result.adminKey);
        }

        // Store encryption parameters
        const storedParams = await EncryptionService.exportParams(encryptionParams);
        EncryptionKeyService.setEncryptionKey(result.id, storedParams);

        return result;
    }

    async getElection(id) {
        const response = await fetch(getApiUrl(`/api/ranked-elections/${id}`), {
            headers: this.getHeaders(id),
        });

        if (!response.ok) {
            if (response.status === 404) {
                throw new NotFoundError('Election not found', 'election');
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        // Check if election is encrypted
        if (result.isEncrypted) {
            // Encryption key MUST exist for encrypted elections
            if (!EncryptionKeyService.hasEncryptionKey(id)) {
                throw new DecryptionError(
                    'Election is encrypted but no encryption key available',
                    id,
                    'missing'
                );
            }

            const storedParams = EncryptionKeyService.getEncryptionKey(id);

            try {
                const encryptionParams = await EncryptionService.importParams(storedParams);
                return await this.decryptElectionData(result, encryptionParams);
            } catch (error) {
                if (error instanceof DecryptionError) {
                    // Attempt recovery with URL parameters
                    return await this.handleDecryptionFailure(
                        result,
                        id,
                        this.decryptElectionData.bind(this),
                        storedParams
                    );
                }
                throw error;
            }
        }

        // Election is not encrypted, return as-is
        return result;
    }

    async updateElectionSettings(id, settings) {
        const response = await fetch(getApiUrl(`/api/ranked-elections/${id}`), {
            method: 'PATCH',
            headers: this.getHeaders(id),
            body: JSON.stringify(settings),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    }

    async updateElection(id, updates) {
        let updatesToSend = updates;

        // Encrypt if encryption key exists
        if (EncryptionKeyService.hasEncryptionKey(id)) {
            const storedParams = EncryptionKeyService.getEncryptionKey(id);
            const encryptionParams = await EncryptionService.importParams(storedParams);
            updatesToSend = await this.encryptElectionData(updates, encryptionParams);
        }

        const response = await fetch(getApiUrl(`/api/ranked-elections/${id}`), {
            method: 'PATCH',
            headers: this.getHeaders(id),
            body: JSON.stringify(updatesToSend),
        });

        if (!response.ok) {
            if (response.status === 403) {
                throw new Error('Forbidden: You do not have permission to modify this election');
            }
            if (response.status === 409) {
                const errorMessage = response.headers.get('X-Error-Message');
                throw new Error(errorMessage || 'Cannot delete option with votes');
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        // Check if election is encrypted
        if (result.isEncrypted) {
            // Encryption key MUST exist for encrypted elections
            if (!EncryptionKeyService.hasEncryptionKey(id)) {
                throw new DecryptionError(
                    'Election is encrypted but no encryption key available',
                    id,
                    'missing'
                );
            }

            const storedParams = EncryptionKeyService.getEncryptionKey(id);

            try {
                const encryptionParams = await EncryptionService.importParams(storedParams);
                return await this.decryptElectionData(result, encryptionParams);
            } catch (error) {
                if (error instanceof DecryptionError) {
                    // Attempt recovery with URL parameters
                    return await this.handleDecryptionFailure(
                        result,
                        id,
                        this.decryptElectionData.bind(this),
                        storedParams
                    );
                }
                throw error;
            }
        }

        // Election is not encrypted, return as-is
        return result;
    }

    async getOptionVoteInfo(electionId, optionId) {
        const response = await fetch(getApiUrl(`/api/ranked-elections/${electionId}/options/${optionId}/vote-info`), {
            method: 'GET',
            headers: this.getHeaders(electionId),
        });

        if (!response.ok) {
            if (response.status === 403) {
                throw new Error('Forbidden: You do not have permission to view this information');
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    }

    async deleteElection(id) {
        const response = await fetch(getApiUrl(`/api/ranked-elections/${id}`), {
            method: 'DELETE',
            headers: this.getHeaders(id),
        });

        if (!response.ok) {
            if (response.status === 403) {
                throw new Error('Forbidden: You do not have permission to delete this election');
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return true;
    }

    async cloneElection(originalElectionId, newTitle) {
        // First, get the original election data
        const originalElection = await this.getElection(originalElectionId);
        
        // Prepare the data for the cloned election
        const clonedElectionData = {
            title: newTitle,
            description: originalElection.description,
            options: originalElection.options.map(option => ({
                name: option.name
            })),
            // Clone other settings if needed
            isProtected: originalElection.isProtected ?? true,
            adminOnlyOptionModifications: originalElection.adminOnlyOptionModifications ?? true
        };

        // Create the new election using the existing createElection method
        const clonedElection = await this.createElection(clonedElectionData);
        
        return clonedElection;
    }

    async getElectionResult(id) {
        // Fetch election to check if it's encrypted
        const election = await this.getElection(id);

        const response = await fetch(getApiUrl(`/api/ranked-elections/${id}/result`), {
            headers: this.getHeaders(id),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        // Check if election is encrypted
        if (election.isEncrypted) {
            // Encryption key MUST exist for encrypted elections
            if (!EncryptionKeyService.hasEncryptionKey(id)) {
                throw new DecryptionError(
                    'Election is encrypted but no encryption key available',
                    id,
                    'missing'
                );
            }

            const storedParams = EncryptionKeyService.getEncryptionKey(id);

            try {
                const encryptionParams = await EncryptionService.importParams(storedParams);
                return await this.decryptElectionData(result, encryptionParams);
            } catch (error) {
                if (error instanceof DecryptionError) {
                    // Attempt recovery with URL parameters
                    return await this.handleDecryptionFailure(
                        result,
                        id,
                        this.decryptElectionData.bind(this),
                        storedParams
                    );
                }
                throw error;
            }
        }

        // Election is not encrypted, return as-is
        return result;
    }

    // Vote API methods
    async createVote(electionId, voteData) {
        // Fetch election to check if it's encrypted
        const election = await this.getElection(electionId);

        let voteDataToSend = voteData;

        // Encrypt if election is encrypted
        if (election.isEncrypted) {
            // Encryption key MUST exist for encrypted elections
            if (!EncryptionKeyService.hasEncryptionKey(electionId)) {
                throw new DecryptionError(
                    'Election is encrypted but no encryption key available',
                    electionId,
                    'missing'
                );
            }

            const storedParams = EncryptionKeyService.getEncryptionKey(electionId);
            const encryptionParams = await EncryptionService.importParams(storedParams);
            voteDataToSend = await this.encryptVoteData(voteData, encryptionParams);
        }

        const response = await fetch(getApiUrl(`/api/ranked-elections/${electionId}/votes`), {
            method: 'POST',
            headers: this.getHeaders(electionId),
            body: JSON.stringify(voteDataToSend),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        // Store admin key for vote
        if (result.adminKey) {
            AdminKeyService.setVoteAdminKey(result.id, result.adminKey);
        }

        // Decrypt response if election is encrypted
        if (election.isEncrypted) {
            // Encryption key MUST exist for encrypted elections
            if (!EncryptionKeyService.hasEncryptionKey(electionId)) {
                throw new DecryptionError(
                    'Election is encrypted but no encryption key available',
                    electionId,
                    'missing'
                );
            }

            const storedParams = EncryptionKeyService.getEncryptionKey(electionId);

            try {
                const encryptionParams = await EncryptionService.importParams(storedParams);
                return await this.decryptVoteData(result, encryptionParams);
            } catch (error) {
                if (error instanceof DecryptionError) {
                    // Attempt recovery with URL parameters
                    return await this.handleDecryptionFailure(
                        result,
                        electionId,
                        this.decryptVoteData.bind(this),
                        storedParams
                    );
                }
                throw error;
            }
        }

        // Vote is not encrypted, return as-is
        return result;
    }

    async getVote(electionId, voteId) {
        // Fetch election to check if it's encrypted
        const election = await this.getElection(electionId);

        const response = await fetch(getApiUrl(`/api/ranked-elections/${electionId}/votes/${voteId}`), {
            headers: this.getHeaders(electionId, voteId),
        });

        if (!response.ok) {
            if (response.status === 404) {
                throw new NotFoundError('Vote not found', 'vote');
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        // Decrypt if election is encrypted
        if (election.isEncrypted) {
            // Encryption key MUST exist for encrypted elections
            if (!EncryptionKeyService.hasEncryptionKey(electionId)) {
                throw new DecryptionError(
                    'Election is encrypted but no encryption key available',
                    electionId,
                    'missing'
                );
            }

            const storedParams = EncryptionKeyService.getEncryptionKey(electionId);

            try {
                const encryptionParams = await EncryptionService.importParams(storedParams);
                return await this.decryptVoteData(result, encryptionParams);
            } catch (error) {
                if (error instanceof DecryptionError) {
                    // Attempt recovery with URL parameters
                    return await this.handleDecryptionFailure(
                        result,
                        electionId,
                        this.decryptVoteData.bind(this),
                        storedParams
                    );
                }
                throw error;
            }
        }

        // Vote is not encrypted, return as-is
        return result;
    }

    async getVotes(electionId) {
        // Fetch election to check if it's encrypted
        const election = await this.getElection(electionId);

        const response = await fetch(getApiUrl(`/api/ranked-elections/${electionId}/votes`), {
            headers: this.getHeaders(electionId),
        });

        if (!response.ok) {
            if (response.status === 404) {
                throw new NotFoundError('Votes not found', 'election');
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        // Decrypt if election is encrypted
        if (election.isEncrypted) {
            // Encryption key MUST exist for encrypted elections
            if (!EncryptionKeyService.hasEncryptionKey(electionId)) {
                throw new DecryptionError(
                    'Election is encrypted but no encryption key available',
                    electionId,
                    'missing'
                );
            }

            const storedParams = EncryptionKeyService.getEncryptionKey(electionId);

            try {
                const encryptionParams = await EncryptionService.importParams(storedParams);
                return await Promise.all(
                    result.map(vote => this.decryptVoteData(vote, encryptionParams))
                );
            } catch (error) {
                if (error instanceof DecryptionError) {
                    // Attempt recovery with URL parameters
                    // Create a wrapper function for decrypting multiple votes
                    // Note: params are already imported by handleDecryptionFailure
                    const decryptVotes = async (votes, importedParams) => {
                        return await Promise.all(
                            votes.map(vote => this.decryptVoteData(vote, importedParams))
                        );
                    };
                    return await this.handleDecryptionFailure(
                        result,
                        electionId,
                        decryptVotes,
                        storedParams
                    );
                }
                throw error;
            }
        }

        // Votes are not encrypted, return as-is
        return result;
    }

    async updateVote(electionId, voteId, voteData) {
        // Fetch election to check if it's encrypted
        const election = await this.getElection(electionId);

        let voteDataToSend = voteData;

        // Encrypt if election is encrypted
        if (election.isEncrypted) {
            // Encryption key MUST exist for encrypted elections
            if (!EncryptionKeyService.hasEncryptionKey(electionId)) {
                throw new DecryptionError(
                    'Election is encrypted but no encryption key available',
                    electionId,
                    'missing'
                );
            }

            const storedParams = EncryptionKeyService.getEncryptionKey(electionId);
            const encryptionParams = await EncryptionService.importParams(storedParams);
            voteDataToSend = await this.encryptVoteData(voteData, encryptionParams);
        }

        const response = await fetch(getApiUrl(`/api/ranked-elections/${electionId}/votes/${voteId}`), {
            method: 'PUT',
            headers: this.getHeaders(electionId, voteId),
            body: JSON.stringify(voteDataToSend),
        });

        if (!response.ok) {
            if (response.status === 403) {
                throw new Error('Forbidden: You do not have permission to modify this vote');
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        // Decrypt response if election is encrypted
        if (election.isEncrypted) {
            // Encryption key MUST exist for encrypted elections
            if (!EncryptionKeyService.hasEncryptionKey(electionId)) {
                throw new DecryptionError(
                    'Election is encrypted but no encryption key available',
                    electionId,
                    'missing'
                );
            }

            const storedParams = EncryptionKeyService.getEncryptionKey(electionId);

            try {
                const encryptionParams = await EncryptionService.importParams(storedParams);
                return await this.decryptVoteData(result, encryptionParams);
            } catch (error) {
                if (error instanceof DecryptionError) {
                    // Attempt recovery with URL parameters
                    return await this.handleDecryptionFailure(
                        result,
                        electionId,
                        this.decryptVoteData.bind(this),
                        storedParams
                    );
                }
                throw error;
            }
        }

        // Vote is not encrypted, return as-is
        return result;
    }

    async deleteVote(electionId, voteId) {
        const response = await fetch(getApiUrl(`/api/ranked-elections/${electionId}/votes/${voteId}`), {
            method: 'DELETE',
            headers: this.getHeaders(electionId, voteId),
        });

        if (!response.ok) {
            if (response.status === 403) {
                throw new Error('Forbidden: You do not have permission to delete this vote');
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return true;
    }
}

const apiService = new ApiService();
export default apiService;
export { NotFoundError };
